import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ShareButtons from '../../components/ShareButtons';
import MedicalReviewByline from '../../components/MedicalReviewByline';
import RelatedResources from '../../components/RelatedResources';
import useBlogViews from '../../hooks/useBlogViews';
import { 
  Calendar, 
  Clock, 
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  ChevronDown,
  User,
  BookOpen,
  Eye
} from 'lucide-react';

const BlogArticlePregnancyDiet = () => {
  const [openFaq, setOpenFaq] = React.useState(null);
  const { formattedViews } = useBlogViews('best-diet-plan-for-pregnancy');
  const articleUrl = 'https://tcubefitness.com/blog/best-diet-plan-for-pregnancy';
  const articleTitle = 'Best Diet Plan for Pregnancy: A Complete Trimester-by-Trimester Guide';

  const faqs = [
    {
      question: "How many extra calories do I need during pregnancy?",
      answer: "Calorie needs vary by trimester. In the first trimester, you typically do not need extra calories. During the second trimester, add approximately 340 calories per day. In the third trimester, add about 450 calories daily. These are general guidelines; your specific needs depend on your pre-pregnancy weight, activity level, and whether you are carrying multiples. Focus on nutrient-dense foods rather than just increasing quantity."
    },
    {
      question: "Which foods should I completely avoid during pregnancy?",
      answer: "Avoid raw or undercooked meat, fish, and eggs; unpasteurized dairy products and juices; high-mercury fish like shark, swordfish, king mackerel, and tilefish; raw sprouts; and excessive caffeine (limit to 200mg daily). Also avoid alcohol completely throughout pregnancy. When in doubt about any food, consult with your healthcare provider."
    },
    {
      question: "Can I follow a vegetarian diet during pregnancy?",
      answer: "Yes, a well-planned vegetarian diet can support a healthy pregnancy. Focus on getting adequate protein from legumes, dairy, and eggs (if you eat them). Pay special attention to iron, B12, zinc, and omega-3 fatty acids, as these can be harder to obtain from plant sources. Your doctor may recommend specific supplements to fill any nutritional gaps."
    },
    {
      question: "How do I manage morning sickness through diet?",
      answer: "Eat small, frequent meals rather than large ones. Keep plain crackers by your bed to eat before getting up. Avoid strong smells and greasy or spicy foods. Ginger in various forms (tea, candies, or fresh) can help reduce nausea. Stay hydrated with small sips throughout the day. If morning sickness is severe and prevents you from keeping food down, consult your doctor."
    },
    {
      question: "Is it safe to diet for weight loss during pregnancy?",
      answer: "No, pregnancy is not the time for weight loss diets. Restricting calories can deprive your baby of essential nutrients needed for development. Instead, focus on eating nutrient-dense foods and appropriate portion sizes. If you are concerned about weight gain, discuss healthy eating strategies with your healthcare provider. Some women with obesity may be advised to gain less weight, but this should always be supervised medically."
    },
    {
      question: "How much water should I drink during pregnancy?",
      answer: "Aim for 8-12 cups (64-96 ounces) of water daily during pregnancy. Adequate hydration supports increased blood volume, amniotic fluid, and helps prevent constipation and urinary tract infections. You may need more in hot weather or if you are physically active. Monitor your urine color; pale yellow indicates good hydration."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Best Diet Plan for Pregnancy: Complete Trimester-by-Trimester Guide | TCUBE FITNESS</title>
        <meta name="description" content="Comprehensive pregnancy diet plan covering all three trimesters. Learn essential nutrients, foods to eat and avoid, and meal planning tips for a healthy pregnancy. Doctor-approved guidelines." />
        <meta name="keywords" content="pregnancy diet plan, best food during pregnancy, pregnancy nutrition guide, first trimester diet, second trimester diet, third trimester diet, healthy pregnancy food, prenatal nutrition" />
        <link rel="canonical" href="https://tcubefitness.com/blog/best-diet-plan-for-pregnancy" />
        <meta property="og:title" content="Best Diet Plan for Pregnancy: Complete Trimester-by-Trimester Guide" />
        <meta property="og:description" content="Doctor-approved pregnancy nutrition guide covering all trimesters with practical meal planning tips." />
        <meta property="og:url" content="https://tcubefitness.com/blog/best-diet-plan-for-pregnancy" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Best Diet Plan for Pregnancy: A Complete Trimester-by-Trimester Guide",
            "author": {
              "@type": "Person",
              "name": "Dr. Pooja Sree",
              "jobTitle": "Medical Lead",
              "worksFor": {
                "@type": "Organization",
                "name": "TCUBE FITNESS"
              }
            },
            "publisher": {
              "@type": "Organization",
              "name": "TCUBE FITNESS",
              "logo": {
                "@type": "ImageObject",
                "url": "https://customer-assets.emergentagent.com/job_8b2c67d7-f3f8-4dd8-ad33-921e8e7f1a96/artifacts/s6b7c1yc_transprant.png"
              }
            },
            "datePublished": "2024-12-01",
            "dateModified": "2024-12-01",
            "description": "Comprehensive pregnancy diet plan covering all three trimesters with essential nutrients, foods to eat and avoid, and meal planning tips."
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-slate-950">
        <Header />
        
        {/* Article Header */}
        <section className="pt-28 pb-12 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-3xl mx-auto">
              <Link to="/blog" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Back to Blog
              </Link>
              
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500/10 text-emerald-400 text-xs font-medium rounded-full mb-4">
                Pregnancy Nutrition
              </span>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Best Diet Plan for Pregnancy: A Complete Trimester-by-Trimester Guide
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-slate-400 text-sm mb-4">
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Dr. Pooja Sree
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  December 2024
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  12 min read
                </span>
                <span className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  {formattedViews} views
                </span>
              </div>
              
              <ShareButtons url={articleUrl} title={articleTitle} />
              <MedicalReviewByline className="mt-4" />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="pb-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto prose prose-invert prose-lg">
              
              <p className="text-xl text-slate-300 leading-relaxed">
                Pregnancy is one of the most nutritionally demanding periods in a woman's life. What you eat directly influences your baby's development, your energy levels, and your body's ability to support a healthy pregnancy. This comprehensive guide covers everything you need to know about eating well during pregnancy, from essential nutrients to practical meal planning tips.
              </p>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">Understanding Pregnancy Nutrition Basics</h2>
              
              <p className="text-slate-300">
                A healthy pregnancy diet is not about eating for two in terms of quantity. Instead, it focuses on eating nutrient-dense foods that provide the building blocks your baby needs to grow and develop properly. The quality of your diet matters far more than the quantity.
              </p>
              
              <p className="text-slate-300">
                During pregnancy, your body requires increased amounts of certain nutrients, including protein, iron, calcium, folate, and omega-3 fatty acids. Meeting these increased needs through food choices and, when necessary, supplements helps ensure optimal outcomes for both you and your baby.
              </p>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">First Trimester Nutrition (Weeks 1-12)</h2>
              
              <p className="text-slate-300">
                The first trimester is a critical period when your baby's major organs and systems begin to form. Despite its importance, many women struggle with nausea and food aversions during this time.
              </p>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Key Nutrients for the First Trimester</h3>
              
              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800 my-6">
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-white">Folate (400-600 mcg daily):</strong> Critical for neural tube development. Found in leafy greens, fortified cereals, legumes, and citrus fruits.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-white">Vitamin B6:</strong> May help reduce nausea. Found in poultry, fish, potatoes, and bananas.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-white">Zinc:</strong> Essential for cell division and growth. Found in meat, shellfish, legumes, and seeds.</span>
                  </li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Managing First Trimester Challenges</h3>
              
              <p className="text-slate-300">
                Morning sickness affects up to 80% of pregnant women. To manage nausea while maintaining nutrition: eat small, frequent meals every 2-3 hours; keep plain crackers or dry toast by your bedside; avoid strong-smelling or greasy foods; try ginger in tea, candies, or fresh form; and stay hydrated with small, frequent sips.
              </p>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">Second Trimester Nutrition (Weeks 13-26)</h2>
              
              <p className="text-slate-300">
                Often called the honeymoon phase of pregnancy, the second trimester typically brings relief from nausea and increased energy. This is an excellent time to focus on building nutrient stores and supporting your baby's rapid growth.
              </p>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Key Nutrients for the Second Trimester</h3>
              
              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800 my-6">
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-white">Protein (75-100g daily):</strong> Supports fetal tissue growth. Found in lean meats, poultry, fish, eggs, dairy, legumes, and tofu.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-white">Iron (27mg daily):</strong> Prevents anemia and supports oxygen transport. Found in red meat, spinach, fortified cereals, and beans.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-white">Calcium (1000mg daily):</strong> Builds baby's bones and teeth. Found in dairy products, fortified plant milks, leafy greens, and almonds.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-white">Vitamin D:</strong> Aids calcium absorption. Found in fortified foods, fatty fish, and sunlight exposure.</span>
                  </li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">Third Trimester Nutrition (Weeks 27-40)</h2>
              
              <p className="text-slate-300">
                During the final trimester, your baby gains most of their birth weight, and their brain undergoes significant development. Your nutritional choices now directly impact your baby's brain development, immune system, and fat stores.
              </p>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Key Nutrients for the Third Trimester</h3>
              
              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800 my-6">
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-white">Omega-3 Fatty Acids (200-300mg DHA):</strong> Critical for brain and eye development. Found in fatty fish (salmon, sardines), walnuts, and flaxseeds.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-white">Fiber:</strong> Helps prevent constipation. Found in whole grains, fruits, vegetables, and legumes.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-white">Vitamin K:</strong> Important for blood clotting. Found in leafy greens, broccoli, and Brussels sprouts.</span>
                  </li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">Foods to Avoid During Pregnancy</h2>
              
              <p className="text-slate-300">
                Certain foods pose risks during pregnancy due to potential contamination or harmful substances. Avoid the following: raw or undercooked meats, seafood, and eggs; unpasteurized dairy products and fresh-squeezed juices; high-mercury fish such as shark, swordfish, king mackerel, and tilefish; deli meats and hot dogs unless heated until steaming; raw sprouts; excessive caffeine (limit to 200mg daily); and all alcohol.
              </p>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">Key ACOG Nutrition Guidelines Simplified</h2>
              
              <p className="text-slate-300 mb-4">
                For pregnant women, we strictly follow ACOG (American College of Obstetricians and Gynecologists) and WHO guidelines adapted to Indian food, dietary patterns, and eating habits.
              </p>
              
              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800 my-6">
                <h4 className="text-lg font-semibold text-white mb-4">Daily Nutritional Requirements</h4>
                <div className="space-y-4 text-slate-300">
                  <div>
                    <p className="text-emerald-400 font-medium">Extra Calories</p>
                    <ul className="ml-4 mt-1 space-y-1">
                      <li>1st trimester: No extra calories required</li>
                      <li>2nd trimester: approximately 340 kcal per day</li>
                      <li>3rd trimester: approximately 450 kcal per day</li>
                    </ul>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-emerald-400 font-medium">Protein</p>
                      <p>70 to 75 grams per day</p>
                    </div>
                    <div>
                      <p className="text-emerald-400 font-medium">Folic Acid</p>
                      <p>600 mcg per day</p>
                    </div>
                    <div>
                      <p className="text-emerald-400 font-medium">Iron</p>
                      <p>27 mg per day</p>
                    </div>
                    <div>
                      <p className="text-emerald-400 font-medium">Calcium</p>
                      <p>1000 mg per day</p>
                    </div>
                    <div>
                      <p className="text-emerald-400 font-medium">Fluids</p>
                      <p>2.5 to 3 liters per day</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-900/20 rounded-xl p-5 border border-blue-500/20 my-6">
                <h4 className="font-semibold text-white mb-2">Key Recommendations</h4>
                <ul className="text-slate-300 space-y-2">
                  <li>• Eat balanced meals that include carbohydrates, protein, and healthy fats</li>
                  <li>• Prefer small and frequent meals</li>
                </ul>
              </div>

              <div className="bg-orange-900/20 rounded-xl p-5 border border-orange-500/20 my-6">
                <h4 className="font-semibold text-orange-400 mb-2">Disclaimer</h4>
                <p className="text-slate-300 text-sm">
                  This diet plan is only a sample and may not be suitable for everyone. Please consult with our medical team for a personalized pregnancy nutrition plan.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">Special Dietary Considerations</h2>
              
              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Vegetarian and Vegan Pregnancies</h3>
              
              <p className="text-slate-300">
                Plant-based diets can support healthy pregnancies with careful planning. Pay special attention to protein combining, iron absorption (pair iron-rich foods with vitamin C), B12 supplementation, and omega-3 sources like algae-based supplements. Work with a healthcare provider or registered dietitian to ensure all nutritional needs are met.
              </p>

              <h3 className="text-xl font-semibold text-white mt-8 mb-3">Gestational Diabetes</h3>
              
              <p className="text-slate-300">
                If you develop gestational diabetes, dietary modifications become essential. Focus on consistent carbohydrate intake distributed evenly throughout the day, choose complex carbohydrates over simple sugars, pair carbohydrates with protein and healthy fats, and monitor blood sugar responses to different foods. Our <Link to="/medical-nutrition-therapy" className="text-emerald-400 hover:underline">Clinical Nutrition programs</Link> provide specialized support for gestational diabetes.
              </p>

              <h2 className="text-2xl font-bold text-white mt-12 mb-4">When to Seek Professional Guidance</h2>
              
              <p className="text-slate-300">
                While general guidelines provide a solid foundation, every pregnancy is unique. Consider working with a healthcare professional if you have pre-existing health conditions, experience severe nausea that prevents adequate nutrition, have a history of eating disorders, are carrying multiples, have specific dietary restrictions or food allergies, or receive a gestational diabetes diagnosis.
              </p>

              <div className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 rounded-xl p-6 border border-emerald-500/20 my-8">
                <h4 className="text-lg font-semibold text-white mb-3">Get Personalized Pregnancy Nutrition Support</h4>
                <p className="text-slate-300 mb-4">
                  Every pregnancy is different. Our <Link to="/pregnancy-nutrition" className="text-emerald-400 hover:underline">gynecologist-supervised pregnancy nutrition program</Link> provides personalized guidance tailored to your specific needs, health history, and dietary preferences.
                </p>
                <Link 
                  to="/submit-details"
                  className="inline-flex items-center gap-2 text-emerald-400 font-medium hover:text-emerald-300"
                >
                  Book a Free Consultation
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

            </div>

            {/* FAQ Section */}
            <div className="max-w-3xl mx-auto mt-16">
              <h2 className="text-2xl font-bold text-white mb-8">Frequently Asked Questions About Pregnancy Diet</h2>
              
              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div 
                    key={idx}
                    className="bg-slate-900/50 rounded-2xl border border-slate-800 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left"
                    >
                      <span className="text-white font-semibold pr-4">{faq.question}</span>
                      <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${openFaq === idx ? 'rotate-180 text-emerald-400' : ''}`} />
                    </button>
                    <div className={`overflow-hidden transition-all ${openFaq === idx ? 'max-h-96' : 'max-h-0'}`}>
                      <p className="px-5 pb-5 text-slate-400 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Articles */}
            <div className="max-w-3xl mx-auto mt-16">
              <h3 className="text-xl font-bold text-white mb-6">Related Articles</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Link to="/blog/fertility-nutrition-hormone-balance" className="bg-slate-900/50 rounded-xl p-4 border border-slate-800 hover:border-pink-500/30 transition-colors">
                  <span className="text-pink-400 text-sm font-medium">Fertility</span>
                  <p className="text-white font-medium mt-1">Fertility Nutrition: Foods That Support Hormone Balance</p>
                </Link>
                <Link to="/blog/pcos-diet-plan-complete-guide" className="bg-slate-900/50 rounded-xl p-4 border border-slate-800 hover:border-blue-500/30 transition-colors">
                  <span className="text-blue-400 text-sm font-medium">Women's Health</span>
                  <p className="text-white font-medium mt-1">PMOS (previously called PCOS) Diet Plan: Complete Management Guide</p>
                </Link>
              </div>
            </div>
          </div>
        </article>

        <div className="container mx-auto px-4 sm:px-6 pb-20">
          <div className="max-w-3xl mx-auto">
            <RelatedResources slug="best-diet-plan-for-pregnancy" />
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default BlogArticlePregnancyDiet;
